import React, { useCallback, useEffect, useState } from 'react';
import { exerciseLibrary } from '../../services/api';
import { ConfirmDialog, Modal, errorList } from './shared';

/*
 * «Demonstrações de domínio público»: as fotografias de demonstração (posição inicial e final) dos exercícios
 * do catálogo, trazidas do free-exercise-db — base de DOMÍNIO PÚBLICO (Unlicense). Duas fases, as mesmas do
 * servidor:
 *
 *   1. Pré-visualizar — não grava nada. Diz quantos exercícios seriam importados e quais ficam de fora, com o
 *      motivo: sem correspondência segura (não se deduz nada de nomes parecidos), já têm imagem ou vídeo, ou
 *      erro. As listas mostram os nomes para o dono validar antes de decidir.
 *   2. Importar — só depois de uma confirmação explícita. Cada ficheiro entra POR VALIDAR: nada chega ao atleta
 *      antes de uma pessoa o aprovar, no editor do exercício (secção «Imagens»).
 *
 * Uma falha de rede não apaga a pré-visualização já feita: a mensagem diz o que falhou e a importação repete-se
 * pelo mesmo botão — o que já tiver entrado não se duplica (o servidor reconhece o ficheiro pelo conteúdo).
 */

const SHOWN = 10;   // nomes por lista; o resto vai resumido («e mais N»)

const rowsWith = (report, action) => (report?.rows || []).filter((r) => r.action === action);
const first = (list) => list.slice(0, SHOWN);
const remaining = (list) => Math.max(0, list.length - SHOWN);

/** «1 exercício» / «3 exercícios» — a concordância certa em cada número. */
const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

/** Uma lista de nomes (não uma tabela): cada exercício é um bloco que cabe em qualquer largura. */
function NameList({ label, rows, empty }) {
  if (rows.length === 0) return <p className="lib-hint">{empty}</p>;
  return (
    <ul className="lib-batch-list" aria-label={label}>
      {first(rows).map((r) => (
        <li key={r.exerciseKey || r.exerciseName || r.externalId}>
          <div className="lib-batch-head">
            <strong>{r.exerciseName || r.exerciseKey}</strong>
          </div>
          {r.externalName && <div className="lib-hint">Na fonte: {r.externalName}</div>}
          {r.reason && <div className="lib-hint">{r.reason}</div>}
        </li>
      ))}
      {remaining(rows) > 0 && <li><div className="lib-hint">e mais {remaining(rows)}.</div></li>}
    </ul>
  );
}

export default function OpenMediaImportDialog({ onClose, onImported }) {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const preview = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await exerciseLibrary.openMediaPreview();
      setReport(data);
    } catch (e) {
      // A pré-visualização anterior (se existir) fica à vista; fica o alerta e repete-se pelo botão.
      setError(errorList(e, 'Não foi possível consultar a base de domínio público.').join(' '));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => { preview(); }, [preview]);

  const apply = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await exerciseLibrary.openMediaImport();
      setReport(data);
      setConfirming(false);
      onImported?.(data);
    } catch (e) {
      // Nada se apaga: o relatório da pré-visualização continua à vista para se repetir a importação.
      setError(errorList(e, 'Não foi possível importar as demonstrações.').join(' '));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  const applied = !!report?.applied;
  const toImport = rowsWith(report, 'add');
  const skipped = rowsWith(report, 'skip');
  const failed = rowsWith(report, 'error');
  const withoutMatch = report?.withoutMatch || [];
  const count = report?.added ?? 0;
  const importLabel = `Importar para ${count === 1 ? '1 exercício' : `${count} exercícios`}`;

  const footer = (
    <>
      <button type="button" className="lib-btn" onClick={onClose}>Fechar</button>
      <button type="button" className="lib-btn" disabled={busy || applied} onClick={preview}>Pré-visualizar de novo</button>
      <button type="button" className="lib-btn primary" disabled={busy || applied || !report || toImport.length === 0}
        onClick={() => setConfirming(true)}>
        {importLabel}
      </button>
    </>
  );

  return (
    <Modal title="Demonstrações de domínio público" onClose={onClose} footer={footer}>
      <p className="lib-hint">
        Fotografias de demonstração (posição inicial e final) do <strong>free-exercise-db</strong>, uma base de
        domínio público. A pré-visualização não grava nada: mostra o que seria importado e o que fica de fora. O que
        entrar fica <strong>por validar</strong> — só chega aos atletas depois de uma pessoa aprovar.
      </p>

      {error && <div className="lib-notice bad" role="alert">{error}</div>}
      {busy && !report && <div className="lib-hint" role="status">A consultar a base de domínio público…</div>}

      {report && (
        <div data-testid="open-report">
          <div className="lib-banner" role="status">
            <strong>{applied ? 'Importação concluída' : 'Pré-visualização'}</strong>
            <span data-testid="open-total">{plural(report.total, 'exercício do catálogo', 'exercícios do catálogo')}</span>
            <span data-testid="open-added">{plural(report.added, 'exercício', 'exercícios')} {applied ? 'importado' : 'a importar'}</span>
            <span data-testid="open-skipped">{plural(report.skipped, 'ignorado', 'ignorados')}</span>
            <span data-testid="open-errors">{report.errors} com erro</span>
            <span data-testid="open-unmatched">{report.unmatched} sem correspondência segura</span>
          </div>

          <p className="lib-hint">
            Fonte: {report.attribution || 'free-exercise-db'} · licença: {report.license || 'não indicada'}
            {report.sourceUrl && <> · <a href={report.sourceUrl} target="_blank" rel="noopener noreferrer">página de origem</a></>}
          </p>

          {applied ? (
            <>
              <div className="lib-notice warn" role="status" data-testid="open-pending">
                <strong>{plural(report.added, 'exercício ficou', 'exercícios ficaram')} por validar.</strong>{' '}
                As fotografias que entraram (posição inicial e final) não chegam aos atletas antes de uma pessoa as
                aprovar: abra o exercício na lista e, na secção «Imagens», decida o que está «Pendente de validação».
              </div>
              {failed.length > 0 && (
                <>
                  <h3 className="lib-h3">Ficaram por importar, com erro ({failed.length})</h3>
                  <NameList label="Exercícios que ficaram por importar" rows={failed} empty="Nenhum erro." />
                </>
              )}
            </>
          ) : (
            <>
              <h3 className="lib-h3">Seria importado ({toImport.length} exercícios)</h3>
              <NameList label="Exercícios que seriam importados" rows={toImport} empty="Nenhum exercício do catálogo sem imagem tem correspondência na fonte." />

              <h3 className="lib-h3">Fica de fora: sem correspondência segura ({withoutMatch.length})</h3>
              <p className="lib-hint">Nada lhes é tocado. Sem correspondência segura pelo nome em inglês ou pelos alternativos, não se importa nada.</p>
              <NameList label="Exercícios sem correspondência segura" rows={withoutMatch} empty="Todos os exercícios sem imagem encontraram correspondência." />

              <h3 className="lib-h3">Fica de fora: com erro ({failed.length})</h3>
              <NameList label="Exercícios com erro" rows={failed} empty="Nenhum erro." />

              <details className="lib-hint">
                <summary>Fica de fora: ignorados ({skipped.length}) — o motivo de cada um está na lista</summary>
                <NameList label="Exercícios ignorados" rows={skipped} empty="Nenhum." />
              </details>
            </>
          )}
        </div>
      )}

      {confirming && (
        <ConfirmDialog
          title="Importar demonstrações de domínio público"
          message={`Vão ser importadas as fotografias de demonstração (posição inicial e final) de ${count === 1 ? '1 exercício' : `${count} exercícios`} do catálogo, a partir do free-exercise-db (domínio público). Cada ficheiro entra POR VALIDAR e não chega aos atletas antes de uma pessoa o aprovar. Nada é sobreposto: os exercícios que já têm imagem ou vídeo ficam de fora.`}
          confirmLabel="Importar"
          busy={busy}
          onConfirm={apply}
          onCancel={() => setConfirming(false)}
        />
      )}
    </Modal>
  );
}
